import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { taskDetailApi } from "../../backend/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  TaskDialog,
  TaskMessage,
  TaskTextArea,
  TaskTextInput,
} from "./taskFormUi";

const initial = {
  booking_company: "",
  confirmation_no: "",
  pickup_date: "",
  pickup_time: "",
  pickup_from: "",
  dropoff_to: "",
  passenger_name: "",
  passenger_phone: "",
  notes: "",
};

export default function HotelTransferForm({
  projectId,
  taskId,
  isOpen = true,
  onClose = () => {},
  onSave = () => {},
}: any) {
  const [form, setForm] = useState(initial);
  const [passengers, setPassengers] = useState([""]);
  const [rowId, setRowId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const update = (event) =>
    setForm((previous) => ({
      ...previous,
      [event.target.name]: event.target.value,
    }));

  const addPassenger = () => setPassengers((previous) => [...previous, ""]);
  const removePassenger = (index) =>
    setPassengers((previous) =>
      previous.length === 1
        ? [""]
        : previous.filter((_, itemIndex) => itemIndex !== index),
    );
  const editPassenger = (index, value) =>
    setPassengers((previous) =>
      previous.map((name, itemIndex) => (itemIndex === index ? value : name)),
    );

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      if (!isOpen || !taskId || !projectId) return;
      setLoading(true);
      setErrorMessage("");

      const { data, error } = await taskDetailApi.list("hotel-transfers", {
        project_id: projectId,
        task_id: taskId,
        limit: 1,
      });

      if (ignore) return;

      if (error) setErrorMessage(error.message);

      const row = data?.[0];
      if (row) {
        setRowId(row.id);

        const dt = row.pickup_datetime ? new Date(row.pickup_datetime) : null;
        setForm({
          booking_company: row.booking_company ?? "",
          confirmation_no: row.confirmation_no ?? "",
          pickup_date: dt ? dt.toISOString().slice(0, 10) : "",
          pickup_time: dt ? dt.toISOString().slice(11, 16) : "",
          pickup_from: row.pickup_from ?? "",
          dropoff_to: row.dropoff_to ?? "",
          passenger_name: row.passenger_name ?? "",
          passenger_phone: row.passenger_phone ?? "",
          notes: row.notes ?? "",
        });

        let names = [""];
        if (Array.isArray(row.passengers) && row.passengers.length) {
          if (typeof row.passengers[0] === "string") {
            names = row.passengers
              .map((passenger) => passenger ?? "")
              .filter((passenger) => typeof passenger === "string");
          } else {
            names = row.passengers
              .map((passenger) => (passenger?.full_name ?? "").toString())
              .filter(Boolean);
          }
        }
        setPassengers(names.length ? names : [""]);
      } else {
        setRowId(null);
        setForm(initial);
        setPassengers([""]);
      }
      setLoading(false);
    };

    load();
    return () => {
      ignore = true;
    };
  }, [isOpen, taskId, projectId]);

  const save = async (event) => {
    event?.preventDefault?.();
    if (!taskId || !projectId) return;

    setLoading(true);
    setErrorMessage("");

    const pickupDateTime =
      form.pickup_date && form.pickup_time
        ? new Date(`${form.pickup_date}T${form.pickup_time}`)
        : null;

    const passengerNames = passengers
      .map((name) => (name || "").trim())
      .filter(Boolean);

    const payload = {
      project_id: projectId,
      task_id: taskId,
      booking_company: form.booking_company || null,
      confirmation_no: form.confirmation_no || null,
      pickup_datetime: pickupDateTime ? pickupDateTime.toISOString() : null,
      pickup_from: form.pickup_from || null,
      dropoff_to: form.dropoff_to || null,
      passenger_name: form.passenger_name || null,
      passenger_phone: form.passenger_phone || null,
      notes: form.notes || null,
      passengers: passengerNames.length ? passengerNames : null,
    };

    const { data, error } = await taskDetailApi.upsert(
      "hotel-transfers",
      payload,
    );

    if (error) {
      setErrorMessage(error.message || "Failed to save hotel transfer");
    }
    if (data?.[0]) {
      setRowId(data[0].id);
      onSave(data[0]);
    }
    setLoading(false);
  };

  const remove = async () => {
    if (!rowId) return;
    setLoading(true);
    setErrorMessage("");

    const { error } = await taskDetailApi.delete("hotel-transfers", rowId);
    if (error) {
      setErrorMessage(error.message || "Failed to delete hotel transfer");
    } else {
      setRowId(null);
      setForm(initial);
      setPassengers([""]);
      onSave();
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const canSave =
    (form.booking_company || "").trim().length > 0 &&
    passengers.some((name) => (name || "").trim().length > 0);

  return (
    <TaskDialog
      title="Hotel Transfer"
      description="Booking details, pickup timing, passengers, and destination."
      onClose={onClose}
      className="max-h-[90vh] max-w-3xl overflow-y-auto"
    >
      <form onSubmit={save} className="space-y-5">
        <TaskMessage>{errorMessage}</TaskMessage>

        <div className="grid gap-4 sm:grid-cols-2">
          <TaskTextInput
            id="hotel-booking-company"
            name="booking_company"
            label="Booking Company"
            value={form.booking_company}
            onChange={update}
            placeholder="Company name"
            required
          />
          <TaskTextInput
            id="hotel-confirmation-no"
            name="confirmation_no"
            label="Confirmation #"
            value={form.confirmation_no}
            onChange={update}
            placeholder="ABC123"
          />
          <TaskTextInput
            id="hotel-pickup-date"
            type="date"
            name="pickup_date"
            label="Pickup Date"
            value={form.pickup_date}
            onChange={update}
          />
          <TaskTextInput
            id="hotel-pickup-time"
            type="time"
            name="pickup_time"
            label="Pickup Time"
            value={form.pickup_time}
            onChange={update}
          />
          <TaskTextInput
            id="hotel-pickup-from"
            name="pickup_from"
            label="Pickup From"
            value={form.pickup_from}
            onChange={update}
            placeholder="Pickup address"
          />
          <TaskTextInput
            id="hotel-dropoff-to"
            name="dropoff_to"
            label="Dropoff To"
            value={form.dropoff_to}
            onChange={update}
            placeholder="Destination address"
          />
        </div>

        <div className="space-y-3 rounded-md border bg-muted/20 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold">Passengers</h3>
              <p className="text-sm text-muted-foreground">
                Add each passenger name for the transfer provider.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addPassenger}
            >
              <Plus className="h-4 w-4" />
              Add Passenger
            </Button>
          </div>

          <div className="space-y-3">
            {passengers.map((name, index) => (
              <div key={index} className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <div className="space-y-2">
                  <Label htmlFor={`hotel-passenger-${index}`}>Full Name</Label>
                  <Input
                    id={`hotel-passenger-${index}`}
                    value={name}
                    onChange={(event) =>
                      editPassenger(index, event.target.value)
                    }
                    placeholder="e.g. Jane Doe"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => removePassenger(index)}
                    className="w-full sm:w-auto"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TaskTextInput
            id="hotel-passenger-name"
            name="passenger_name"
            label="Primary Passenger"
            value={form.passenger_name}
            onChange={update}
            placeholder="Optional if listed above"
          />
          <TaskTextInput
            id="hotel-passenger-phone"
            name="passenger_phone"
            label="Primary Phone"
            value={form.passenger_phone}
            onChange={update}
            placeholder="Optional"
          />
          <TaskTextArea
            id="hotel-notes"
            name="notes"
            label="Notes"
            value={form.notes}
            onChange={update}
            placeholder="Any instructions..."
            rows={4}
            className="sm:col-span-2"
          />
        </div>

        <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {rowId && (
            <Button
              type="button"
              variant="destructive"
              onClick={remove}
              disabled={loading}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
          <Button type="submit" disabled={loading || !canSave}>
            {rowId ? "Save Changes" : "Create Booking"}
          </Button>
        </div>
      </form>
    </TaskDialog>
  );
}
